import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, FileText, GitCompareArrows, Loader2, Pencil, ShieldAlert, Zap } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import PolicySections from "@/components/PolicyContent";
import { useAuth } from "@/context/AuthContext";
import api, { API_URL, formatApiError } from "@/lib/api";
import { ACK_LABELS, STATUS_LABELS, STATUS_STYLES } from "@/lib/policyLabels";

const CONTROL_ROWS = (p, v) => [
  ["Organisation", "Deluxe Travel Training"],
  ["Policy Number", p.reference],
  ["Version", v?.version || "—"],
  ["Status", STATUS_LABELS[v?.status || p.latest_status] || p.latest_status],
  ["Policy Owner", p.owner_role || "—"],
  ["Author", v?.author_name || p.author_name || "—"],
  ["Approved By", v?.approver_name || "Not yet approved"],
  ["Effective Date", v?.effective_date || "—"],
  ["Next Review", p.next_review_date || "As configured by management"],
  ["Classification", p.classification || "Internal"],
];

export default function PolicyDetailPage() {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("policies:manage");
  const canPublish = hasPermission("policies:publish");
  const [p, setP] = useState(null);
  const [dialog, setDialog] = useState(null); // amend | publish | check | compare
  const [amend, setAmend] = useState({ amendment_type: "minor", change_reason: "", change_summary: "", base_version_id: "" });
  const [pub, setPub] = useState({ effective_date: "", acknowledgement_requirement: "all_applicable" });
  const [answers, setAnswers] = useState({});
  const [checkResult, setCheckResult] = useState(null);
  const [cmp, setCmp] = useState({ v1: "", v2: "", diff: null });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/policies/${policyId}`);
      setP(data);
      if (data.current_version_id && !canManage) {
        api.post(`/policies/${policyId}/open`).catch(() => {});
      }
    } catch (err) {
      toast.error(formatApiError(err));
      navigate("/policies");
    }
  }, [policyId, canManage, navigate]);

  useEffect(() => { load(); }, [load]);

  if (!p) return <div className="flex items-center gap-2 text-slate-600" data-testid="policy-detail-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const v = p.version_detail;
  const isDraftView = canManage && v && v.status !== "published";
  const myAck = p.my_acknowledgement;
  const needsAck = !canManage && p.current_version_id && p.mandatory && !(myAck && myAck.acknowledged_at);

  const act = async (fn, msg) => {
    setBusy(true);
    try {
      await fn();
      if (msg) toast.success(msg);
      setDialog(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const acknowledge = () => act(() => api.post(`/policies/${policyId}/acknowledge`), "Acknowledgement recorded");
  const submitReview = () => act(() => api.post(`/policies/versions/${v.id}/submit-review`), "Submitted for approval");
  const approve = () => act(() => api.post(`/policies/versions/${v.id}/approve`), "Version approved");
  const archive = () => act(() => api.post(`/policies/versions/${v.id}/archive`), "Archived");
  const doAmend = () => act(async () => {
    const { data } = await api.post(`/policies/${policyId}/versions`, {
      amendment_type: amend.amendment_type,
      change_reason: amend.change_reason,
      change_summary: amend.change_summary,
      base_version_id: amend.base_version_id || undefined,
    });
    navigate(`/policies/${policyId}/edit?version=${data.version_id}`);
  }, null);
  const doPublish = () => act(() => api.post(`/policies/versions/${v.id}/publish`, {
    effective_date: pub.effective_date || undefined,
    acknowledgement_requirement: pub.acknowledgement_requirement,
  }), "Published — affected staff have been notified");
  const doCheck = () => act(async () => {
    const { data } = await api.post(`/policies/${policyId}/check/attempt`, {
      answers: p.knowledge_check.map((_, i) => answers[i] ?? -1),
    });
    setCheckResult(data);
  }, null);
  const doCompare = () => act(async () => {
    const { data } = await api.get(`/policies/${policyId}/compare?v1=${cmp.v1}&v2=${cmp.v2}`);
    setCmp((c) => ({ ...c, diff: data }));
  }, null);

  return (
    <div className="space-y-5 max-w-4xl" data-testid="policy-detail">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/policies" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-primary" data-testid="policy-back">
          <ArrowLeft className="h-4 w-4" /> Library
        </Link>
        <div className="ml-auto flex flex-wrap gap-2">
          <a href={`${API_URL}/policies/${policyId}/pdf`} target="_blank" rel="noreferrer" data-testid="policy-pdf-button"
            className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-medium text-slate-700 hover:bg-slate-50">
            <FileText className="h-3.5 w-3.5" /> PDF
          </a>
          {canManage && <Button variant="outline" className="h-9 text-xs" data-testid="policy-compare-button" onClick={() => setDialog("compare")}><GitCompareArrows className="h-3.5 w-3.5 mr-1" /> Compare versions</Button>}
          {canManage && v && v.status === "draft" && (
            <Button className="h-9 text-xs bg-primary text-white" data-testid="policy-edit-button" onClick={() => navigate(`/policies/${policyId}/edit?version=${v.id}`)}>
              <Pencil className="h-3.5 w-3.5 mr-1" /> Edit draft
            </Button>
          )}
          {canManage && <Button variant="outline" className="h-9 text-xs" data-testid="policy-amend-button" onClick={() => setDialog("amend")}>Amend (new version)</Button>}
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm font-semibold text-slate-500" data-testid="policy-reference">{p.reference}</span>
          <Badge className={STATUS_STYLES[v?.status || p.latest_status] || ""} data-testid="policy-status-badge">{STATUS_LABELS[v?.status || p.latest_status]}</Badge>
          {p.requires_formal_review && <Badge className="bg-amber-50 text-amber-700 border-transparent" data-testid="policy-formal-flag">Requires formal organisational review</Badge>}
          {p.mandatory && <Badge className="bg-slate-100 text-slate-600 border-transparent">Mandatory</Badge>}
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1" data-testid="policy-title">{p.title}</h1>
        <p className="text-sm text-slate-600 mt-1">{p.category}{p.owner_role ? ` · Owner: ${p.owner_role}` : ""}</p>
      </div>

      {p.requires_formal_review && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="policy-review-notice">
          This document is an organisational template. Its presence in the library does not constitute legal approval;
          formal organisational review applies before publication or reliance.
        </div>
      )}
      {isDraftView && (
        <div className="rounded-lg border border-slate-300 bg-slate-50 p-3 text-xs text-slate-700" data-testid="policy-draft-banner">
          You are viewing a {STATUS_LABELS[v.status].toLowerCase()} version (v{v.version}) — visible to authorised management only. It is not published to staff.
        </div>
      )}

      {p.is_quick_procedure && (p.quick_steps || []).length > 0 && (
        <Card className="border-2 border-red-200 bg-red-50/40" data-testid="policy-quick-card">
          <CardHeader><CardTitle className="flex items-center gap-2 text-lg text-red-700"><Zap className="h-5 w-5" /> Quick actions</CardTitle></CardHeader>
          <CardContent>
            <ol className="space-y-2">
              {p.quick_steps.map((s, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-slate-800" data-testid={`quick-step-${i}`}>
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
            {p.quick_warning && <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-red-700"><ShieldAlert className="h-4 w-4" /> {p.quick_warning}</p>}
            <p className="mt-2 text-xs text-slate-500">The app never replaces the emergency services — in immediate danger always call 999 first.</p>
          </CardContent>
        </Card>
      )}

      <Card data-testid="policy-control-table">
        <CardHeader><CardTitle className="text-base">Document control</CardTitle></CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            {CONTROL_ROWS(p, v).map(([k, val]) => (
              <div key={k} className="flex gap-2"><dt className="w-32 shrink-0 text-slate-500">{k}</dt><dd className="font-medium text-slate-900">{val}</dd></div>
            ))}
          </dl>
          {v?.change_summary && (
            <p className="mt-3 rounded-md bg-slate-50 border border-border p-2.5 text-xs text-slate-600" data-testid="policy-change-summary">
              <b>This version:</b> {v.change_reason} — {v.change_summary}
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6"><PolicySections sections={v?.sections} /></CardContent>
      </Card>

      {needsAck && (
        <Card className="border-amber-300" data-testid="policy-ack-card">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <p className="text-sm text-slate-700 flex-1 min-w-[220px]">
              By acknowledging you confirm you have opened and read this version. Acknowledgement is a record of receipt, not proof of understanding.
            </p>
            <Button className="h-11 bg-primary text-white" data-testid="policy-acknowledge-button" disabled={busy} onClick={acknowledge}>
              I have read and acknowledge this policy
            </Button>
          </CardContent>
        </Card>
      )}
      {myAck?.acknowledged_at && !canManage && (
        <p className="flex items-center gap-1.5 text-sm text-ukgreen" data-testid="policy-acked-note">
          <CheckCircle2 className="h-4 w-4" /> You acknowledged v{p.current_version} on {new Date(myAck.acknowledged_at).toLocaleDateString()}.
        </p>
      )}
      {p.has_knowledge_check && p.current_version_id && !canManage && (
        <Card data-testid="policy-check-card">
          <CardContent className="p-4 flex flex-wrap items-center gap-3">
            <p className="text-sm text-slate-700 flex-1 min-w-[220px]">This policy has a short knowledge check. Completion is recorded; it is not a measure of competence.</p>
            <Button variant="outline" className="h-11" data-testid="policy-check-open" onClick={() => { setDialog("check"); setCheckResult(null); setAnswers({}); }}>
              Start knowledge check
            </Button>
          </CardContent>
        </Card>
      )}

      <Card data-testid="policy-versions">
        <CardHeader><CardTitle className="text-base">Version history</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {(p.versions || []).map((ver) => (
            <div key={ver.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2.5 text-sm" data-testid={`policy-version-${ver.id}`}>
              <span className="font-mono font-semibold">v{ver.version}</span>
              <Badge className={STATUS_STYLES[ver.status] || ""}>{STATUS_LABELS[ver.status]}</Badge>
              <span className="text-xs text-slate-500">{ver.effective_date || "no effective date"}{ver.approver_name ? ` · approved by ${ver.approver_name}` : ""}</span>
              {ver.change_summary && <span className="text-xs text-slate-500 truncate max-w-[260px]">{ver.change_summary}</span>}
              {canManage && ver.status === "draft" && (
                <Button variant="ghost" className="ml-auto h-8 text-xs" data-testid={`policy-version-edit-${ver.id}`} onClick={() => navigate(`/policies/${policyId}/edit?version=${ver.id}`)}>Edit</Button>
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {canManage && v && (
        <Card data-testid="policy-manager-actions">
          <CardHeader><CardTitle className="text-base">Management actions</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {v.status === "draft" && <Button className="h-10" data-testid="policy-submit-review" disabled={busy} onClick={submitReview}>Submit for approval</Button>}
            {v.status === "awaiting_approval" && canPublish && <Button className="h-10" data-testid="policy-approve" disabled={busy} onClick={approve}>Approve</Button>}
            {(v.status === "approved" || (v.status === "draft" && v.author_name?.includes("initial library"))) && canPublish && (
              <Button className="h-10 bg-ukgreen text-white hover:bg-ukgreen/90" data-testid="policy-publish" disabled={busy} onClick={() => setDialog("publish")}>Publish…</Button>
            )}
            {(v.status === "published" || v.status === "superseded") && canPublish && (
              <Button variant="outline" className="h-10" data-testid="policy-archive" disabled={busy} onClick={archive}>Archive</Button>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={dialog === "amend"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="policy-amend-dialog">
          <DialogHeader>
            <DialogTitle>Amend policy — new draft version</DialogTitle>
            <DialogDescription>The published version is never overwritten. A new draft is created for review, approval and publication.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Amendment type</Label>
              <Select value={amend.amendment_type} onValueChange={(val) => setAmend((a) => ({ ...a, amendment_type: val }))}>
                <SelectTrigger className="h-11" data-testid="amend-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="minor">Minor (wording, formatting, contacts)</SelectItem>
                  <SelectItem value="major">Major (procedure, responsibilities, practice)</SelectItem>
                  <SelectItem value="urgent">Urgent operational amendment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Reason for change *</Label>
              <Textarea rows={2} data-testid="amend-reason" value={amend.change_reason} onChange={(e) => setAmend((a) => ({ ...a, change_reason: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Summary of changes *</Label>
              <Textarea rows={3} data-testid="amend-summary" value={amend.change_summary} onChange={(e) => setAmend((a) => ({ ...a, change_summary: e.target.value }))} /></div>
            {(p.versions || []).length > 0 && (
              <div className="space-y-1.5">
                <Label>Base version (default: current)</Label>
                <Select value={amend.base_version_id || "current"} onValueChange={(val) => setAmend((a) => ({ ...a, base_version_id: val === "current" ? "" : val }))}>
                  <SelectTrigger className="h-11" data-testid="amend-base"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="current">Current published version</SelectItem>
                    {p.versions.filter((x) => x.status === "superseded" || x.status === "archived").map((x) => (
                      <SelectItem key={x.id} value={x.id}>Restore from v{x.version} ({STATUS_LABELS[x.status]})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500">Restoring an older version creates a new draft based on it — history is never deleted.</p>
              </div>
            )}
            <Button className="w-full h-11" data-testid="amend-submit" disabled={busy || amend.change_reason.length < 3 || amend.change_summary.length < 3} onClick={doAmend}>
              Create draft version
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "publish"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-md" data-testid="policy-publish-dialog">
          <DialogHeader>
            <DialogTitle>Publish v{v?.version}</DialogTitle>
            <DialogDescription>The previous published version becomes Superseded and is preserved. Affected staff are notified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Effective date</Label>
              <Input type="date" className="h-11" data-testid="publish-effective-date" value={pub.effective_date} onChange={(e) => setPub((x) => ({ ...x, effective_date: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Do staff need to acknowledge this version?</Label>
              <Select value={pub.acknowledgement_requirement} onValueChange={(val) => setPub((x) => ({ ...x, acknowledgement_requirement: val }))}>
                <SelectTrigger className="h-11" data-testid="publish-ack-req"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  {Object.entries(ACK_LABELS).map(([key, label]) => <SelectItem key={key} value={key}>{label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500">Previous acknowledgements are always preserved.</p>
            </div>
            <Button className="w-full h-11 bg-ukgreen text-white hover:bg-ukgreen/90" data-testid="publish-confirm" disabled={busy} onClick={doPublish}>Publish</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "check"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="policy-check-dialog">
          <DialogHeader>
            <DialogTitle>Knowledge check — {p.reference}</DialogTitle>
            <DialogDescription>Pass mark: {p.check_pass_mark} of {p.knowledge_check.length}. A completion record only — not a measure of competence.</DialogDescription>
          </DialogHeader>
          {checkResult ? (
            <div className="space-y-3 text-center" data-testid="check-result">
              <p className={`text-2xl font-bold ${checkResult.passed ? "text-ukgreen" : "text-amber-700"}`}>{checkResult.score}/{checkResult.total}</p>
              <p className="text-sm text-slate-600">{checkResult.passed ? "Completed — recorded." : "Not passed this time — re-read the policy and try again."}</p>
              <Button variant="outline" className="h-10" data-testid="check-close" onClick={() => setDialog(null)}>Close</Button>
            </div>
          ) : (
            <div className="space-y-4">
              {(p.knowledge_check || []).map((q, i) => (
                <div key={i} className="space-y-2" data-testid={`check-q-${i}`}>
                  <p className="text-sm font-medium text-slate-900">{i + 1}. {q.question}</p>
                  <div className="space-y-1.5">
                    {(q.options || []).map((opt, oi) => (
                      <button key={oi} type="button" data-testid={`check-q${i}-opt${oi}`} onClick={() => setAnswers((a) => ({ ...a, [i]: oi }))}
                        className={`block w-full rounded-md border px-3 py-2 text-left text-sm transition-colors duration-150 ${answers[i] === oi ? "border-primary bg-primary/5 text-slate-900" : "border-border text-slate-700 hover:bg-slate-50"}`}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <Button className="w-full h-11" data-testid="check-submit" disabled={busy || Object.keys(answers).length !== (p.knowledge_check || []).length} onClick={doCheck}>
                Submit answers
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "compare"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="policy-compare-dialog">
          <DialogHeader>
            <DialogTitle>Compare versions</DialogTitle>
            <DialogDescription>Line-by-line comparison of two versions of {p.reference}.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 items-end">
            <Select value={cmp.v1} onValueChange={(val) => setCmp((c) => ({ ...c, v1: val, diff: null }))}>
              <SelectTrigger className="h-10 w-40" data-testid="compare-v1"><SelectValue placeholder="Version A" /></SelectTrigger>
              <SelectContent className="bg-white">{p.versions.map((x) => <SelectItem key={x.id} value={x.id}>v{x.version}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={cmp.v2} onValueChange={(val) => setCmp((c) => ({ ...c, v2: val, diff: null }))}>
              <SelectTrigger className="h-10 w-40" data-testid="compare-v2"><SelectValue placeholder="Version B" /></SelectTrigger>
              <SelectContent className="bg-white">{p.versions.map((x) => <SelectItem key={x.id} value={x.id}>v{x.version}</SelectItem>)}</SelectContent>
            </Select>
            <Button className="h-10" data-testid="compare-run" disabled={!cmp.v1 || !cmp.v2 || cmp.v1 === cmp.v2 || busy} onClick={doCompare}>Compare</Button>
          </div>
          {cmp.diff && (
            <div className="mt-3 rounded-md border border-border bg-slate-50 p-3 font-mono text-xs max-h-96 overflow-y-auto" data-testid="compare-result">
              <p className="mb-2 font-sans text-xs text-slate-500">v{cmp.diff.v1} → v{cmp.diff.v2} (green = added, red = removed)</p>
              {cmp.diff.diff.filter((d) => d.type !== "same").length === 0 ? (
                <p className="text-slate-500">No content differences.</p>
              ) : cmp.diff.diff.filter((d) => d.type !== "same").map((d, i) => (
                <p key={i} className={d.type === "add" ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800 line-through"}>
                  {d.type === "add" ? "+ " : "- "}{d.text}
                </p>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
